import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Typography,
  Switch,
  FormControlLabel,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PeopleIcon from "@mui/icons-material/People";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { getSessionSlotTypeLabel } from "../../../utils/programTimeSlotUtils";

interface SessionCardProps {
  session: any;
  onEdit: (session: any) => void;
  onDelete: (sessionId: string) => void;
  canEdit?: boolean;
  onToggleActive?: (id: string, current: boolean) => void;
}

export const SessionCard = ({
  session,
  onEdit,
  onDelete,
  canEdit = true,
  onToggleActive,
}: SessionCardProps) => {
  // Format day name
  const formatDayName = (day: string) => {
    return day.charAt(0).toUpperCase() + day.slice(1);
  };

  // Format date range
  const formatDateRange = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return "";
    return `${startDate} - ${endDate}`;
  };

  return (
    <Card
      sx={{
        borderRadius: 3,
        transition: "all 0.2s ease",
        border: "1px solid #e0e0e0",
        overflow: "hidden",
        height: "100%",
        width: "300px",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          boxShadow: 4,
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Image Section - Responsive 16:9 Aspect Ratio */}
      {session.imageUrl && (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: { xs: 120, sm: 140, md: 160 },
            overflow: "hidden",
            borderRadius: "12px 12px 0 0",
            backgroundColor: "#f5f5f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={session.imageUrl}
            alt={session.name}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "transform 0.3s ease-in-out",
            }}
          />
        </Box>
      )}

      {!session.imageUrl && (
        <Box
          sx={{
            width: "100%",
            height: { xs: 120, sm: 140, md: 160 },
            backgroundColor:
              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "12px 12px 0 0",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            color="white"
            sx={{
              textAlign: "center",
              textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
              zIndex: 1,
            }}
          >
            {session.name}
          </Typography>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "linear-gradient(135deg, rgba(102, 126, 234, 0.8) 0%, rgba(118, 75, 162, 0.8) 100%)",
              pointerEvents: "none",
            }}
          />
        </Box>
      )}

      <CardContent
        sx={{ p: 3, flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        <Stack spacing={2.5} sx={{ flexGrow: 1 }}>
          {/* Header Section */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{
                  mb: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {session.name || "Untitled Session"}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={getSessionSlotTypeLabel(session.sessionSlotType)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={session.isActive ? "Active" : "Inactive"}
                  size="small"
                  color={session.isActive ? "success" : "default"}
                  variant={session.isActive ? "filled" : "outlined"}
                />
              </Stack>
            </Box>

            {/* Action + Toggle */}
            {canEdit && (
              <Stack spacing={0.5} alignItems="flex-end">
                <Stack direction="row" spacing={0.5}>
                  <IconButton
                    color="primary"
                    onClick={() => onEdit(session)}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      "&:hover": {
                        backgroundColor: "primary.light",
                        color: "white",
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => {
                      const sessionId = session.id || session.sessionId;
                      if (sessionId) onDelete(sessionId);
                    }}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      "&:hover": {
                        backgroundColor: "error.light",
                        color: "white",
                      },
                    }}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Stack>
                {onToggleActive && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(session.isActive)}
                        onChange={() =>
                          onToggleActive(
                            session.id || session.sessionId,
                            Boolean(session.isActive)
                          )
                        }
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Typography
                        variant="caption"
                        color={session.isActive ? "success.main" : "error.main"}
                      >
                        {session.isActive ? "Active" : "Inactive"}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                )}
              </Stack>
            )}
          </Stack>

          {/* Date Section */}
          {session.startDate && session.endDate ? (
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <CalendarMonthIcon
                fontSize="small"
                color="action"
                sx={{ mt: 0.5 }}
              />
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={500}
                >
                  {new Date(session.startDate).toLocaleString([], {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  →
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={500}
                >
                  {new Date(session.endDate).toLocaleString([], {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </Typography>
              </Box>
            </Stack>
          ) : (
            <>
              {Object.keys(session.dayOfWeekDateRanges || {}).length > 0 && (
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <CalendarMonthIcon
                    fontSize="small"
                    color="action"
                    sx={{ mt: 0.5 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontWeight={500}
                      sx={{ mb: 0.5 }}
                    >
                      Recurring Schedule:
                    </Typography>
                    <Stack spacing={0.5}>
                      {Object.entries(session.dayOfWeekDateRanges || {}).map(
                        ([day, range]: [string, any]) => (
                          <Typography
                            key={day}
                            variant="body2"
                            color="text.secondary"
                            sx={{ pl: 1 }}
                          >
                            <strong>{formatDayName(day)}:</strong>{" "}
                            {formatDateRange(range?.startDate, range?.endDate)}
                          </Typography>
                        )
                      )}
                    </Stack>
                  </Box>
                </Stack>
              )}
            </>
          )}

          {/* Age Groups Section */}
          {Array.isArray(session.ageGroups) && session.ageGroups.length > 0 && (
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={500}
                sx={{ mb: 1 }}
              >
                Age Groups:
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                sx={{ gap: 1 }}
              >
                {session.ageGroups.map((g: string, i: number) => (
                  <Chip
                    key={i}
                    label={g}
                    size="small"
                    variant="outlined"
                    color="default"
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
